import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";

// Info-icon tooltip that renders its popover into document.body so it is never
// clipped by overflow:hidden ancestor containers. The icon follows the site's
// design-system palette and shows a hover/focus tint in the accent colour.
export function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.top, left: r.left + r.width / 2 });
  }, []);

  useEffect(() => {
    if (!open) return;
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open, recompute]);

  const active = open || hovered;

  return (
    <>
      <span
        ref={ref}
        tabIndex={0}
        role="button"
        aria-label="More information"
        onMouseEnter={() => { setHovered(true); recompute(); setOpen(true); }}
        onMouseLeave={() => { setHovered(false); setOpen(false); }}
        onFocus={() => { recompute(); setOpen(true); }}
        onBlur={() => setOpen(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: active ? "#FFE9F6" : "#F0F0F0",
          color: active ? "#F90091" : "#7b7b7b",
          fontSize: 10,
          fontWeight: 600,
          fontStyle: "italic",
          fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
          cursor: "help",
          lineHeight: 1,
          outline: "none",
          flexShrink: 0,
          userSelect: "none",
          transition: "background 0.15s, color 0.15s",
        }}
      >
        i
      </span>
      {open && pos && ReactDOM.createPortal(
        <span
          role="tooltip"
          style={{
            position: "fixed",
            top: pos.top - 10,
            left: pos.left,
            transform: "translate(-50%, -100%)",
            background: "#151515",
            color: "#FFFFFF",
            fontSize: 12,
            fontWeight: 400,
            fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
            lineHeight: 1.45,
            padding: "7px 11px",
            borderRadius: 8,
            whiteSpace: "normal",
            width: "max-content",
            maxWidth: 240,
            textAlign: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.16)",
            pointerEvents: "none",
            zIndex: 10000,
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </>
  );
}
